// models
import { AppEnvironment, ExternalUserRoles, IUser, UserRoles, VendorType } from '../models';
import { deleteCookie } from './cookies';
import { getEnvironment } from './environment';
import { getLocalStorage, removeLocalStorage, setLocalStorage, LS_KEY } from './local-storage';

/**
 * Set user to local storage
 * @param user ILoginUser
 */
export const setUserLocalStorage = (user: IUser | undefined): void => {
  if (user) {
    setLocalStorage(LS_KEY, user);
  }
};

/**
 * Remove user from local storage
 */
export const removeUserLocalStorage = async (): Promise<void> => {
  await removeLocalStorage(LS_KEY);
};

/**
 * Get user from local storage
 */
export const getUserLocalStorage = async (): Promise<IUser | undefined> => {
  return await getLocalStorage(LS_KEY);
};

/**
 * Set access token and refresh token cookies for a user
 * @param accessToken string
 * @param tokenExpiresAt string
 */

/**
 * When logging a user out, remove their tokens and local storage
 */

export const userLogout = async () => {
  deleteCookie(
    'BuildManager-Dev-V2',
    '/',
    getEnvironment() === AppEnvironment.Local ? 'localhost' : '.stonemartinbuilders.com'
  );
  deleteCookie(
    'BuildManager-Dev',
    '/',
    getEnvironment() === AppEnvironment.Local ? 'localhost' : '.stonemartinbuilders.com'
  );
  deleteCookie('BuildManager', '/', '.stonemartinbuilders.com');
  deleteCookie('BuildManager-V2', '/', '.stonemartinbuilders.com');
  deleteCookie('ASP.NET_SessionId', '/', '.stonemartinbuilders.com');
  await removeUserLocalStorage();
};

export const isAllowedToBeEditedOrDeleted = (name: string): boolean => {
  if (name === UserRoles.BillingAdmin) {
    return false;
  }
  if (name === UserRoles.FinancialAdmin) {
    return false;
  }
  if (name === UserRoles.WarrantyAdmin) {
    return false;
  }
  if (name === UserRoles.GlobalAdmin) {
    return false;
  }
  if (name === UserRoles.ContentManager) {
    return false;
  }
  return true;
};

export const isPlaceOrderAdmin = (user: IUser): boolean => {
  return user?.userRoles?.some(role =>
    [
      UserRoles.BLAdmin,
      UserRoles.BLDevelopment,
      UserRoles.BLField,
      UserRoles.BLFieldAdmin
    ].includes(role as UserRoles)
  );
};

export const isAdmin = (user: IUser): boolean => {
  return user?.userRoles?.includes(UserRoles.BLAdmin);
};

export const getLoggedInUserPartnerTypes = (user: IUser | null): string[] => {
  return user?.externalUser?.partners?.map(part => part.type.toLowerCase()) || [];
};

export const isSubcontractor = (user: IUser | null, type?: string): boolean => {
  const userTypes = getLoggedInUserPartnerTypes(user);
  return (
    userTypes?.includes(VendorType.TRADE_PARTNER_AFFILIATE) ||
    userTypes?.includes(VendorType.SUBCONTRACTOR_CURRENT) ||
    user?.userRoles.map(r => r?.toLowerCase()).includes(VendorType.BL_SUBCONTRACTOR_CURRENT) ||
    type?.toLowerCase() === VendorType.TRADE_PARTNER_AFFILIATE ||
    type?.toLowerCase() === VendorType.SUBCONTRACTOR_CURRENT ||
    type?.toLowerCase() === VendorType.BL_SUBCONTRACTOR_CURRENT ||
    type?.toLowerCase() === VendorType.SUB_INACTIVE_UNAPPROVED
  );
};

export const isVendorPartner = (user: IUser | null, type?: string): boolean => {
  const userTypes = getLoggedInUserPartnerTypes(user);
  return (
    userTypes?.includes(VendorType.TRADE_PARTNER_AFFILIATE) ||
    userTypes?.includes(VendorType.SUBCONTRACTOR_CURRENT) ||
    user?.userRoles.map(r => r?.toLowerCase()).includes(VendorType.BL_SUBCONTRACTOR_CURRENT) ||
    type?.toLowerCase() === VendorType.TRADE_PARTNER_AFFILIATE ||
    type?.toLowerCase() === VendorType.SUBCONTRACTOR_CURRENT ||
    type?.toLowerCase() === VendorType.BL_SUBCONTRACTOR_CURRENT ||
    type?.toLowerCase() === VendorType.BLACK_LISTED_VENDOR ||
    type?.toLowerCase() === VendorType.CONSTRUCTION_LENDERS ||
    type?.toLowerCase() === VendorType.GOVERNMENT ||
    type?.toLowerCase() === VendorType.MATERIAL_SUPPLIER_ACTIVE ||
    type?.toLowerCase() === VendorType.MATERIAL_SUPPLIER_INACTIVE ||
    type?.toLowerCase() === VendorType.MISC ||
    type?.toLowerCase() === VendorType.MORTGAGE_LENDER ||
    type?.toLowerCase() === VendorType.SUB_INACTIVE_UNAPPROVED
  );
};

export const isHomeowner = (user: IUser): boolean => {
  return user?.userRoles?.includes(UserRoles.BLClient);
};

export const isFieldUser = (user: IUser): boolean => {
  return user?.userRoles?.includes(UserRoles.BLField);
};

export const isDevelopmentUser = (user: IUser): boolean => {
  return user?.userRoles?.includes(UserRoles.BLDevelopment);
};

export const isInternalUser = (user: IUser): boolean => {
  // I don't think this completely encompasses all internal users
  if (isFieldUser(user) || isDevelopmentUser(user) || isAdmin(user)) {
    return true;
  } else {
    return false;
  }
};

export const isExternalUser = (user: IUser): boolean => {
  // Check to see if the user object has external user data, internal users do not have this data
  return !!user?.externalUser?.personId;
};

export const isAllowedToSeeHomeownerPortal = (user: IUser): boolean => {
  // user is a homeowner
  return (
    isHomeowner(user) ||
    // user is a sales agent, sales, sales admin, or sales coordinator
    (isExternalUser(user) &&
      // external user exclusions
      (!!user?.userRoles?.includes(UserRoles.BLAgent) ||
        !!user?.userRoles?.includes(UserRoles.BLSales) ||
        !!user?.userRoles?.includes(UserRoles.BLSalesAdmin) ||
        !!user?.userRoles?.includes(UserRoles.BLSalesCoordinator))) ||
    // show for all internal users
    !isExternalUser(user)
  );
};

export const isLenderOrAttorney = (user: IUser): boolean => {
  return user?.userRoles?.some(role =>
    [UserRoles.BLAttorney, UserRoles.BLLender].includes(role as UserRoles)
  );
};

export const isLenderOrLenderProcessor = (user: IUser): boolean => {
  return user?.userRoles?.some(role =>
    [UserRoles.BLLender, ExternalUserRoles.Lender, ExternalUserRoles.LenderProcessor].includes(
      role as UserRoles
    )
  );
};

export const isThriveHomeowner = (user: IUser): boolean => {
  const lotType = user?.externalUser?.latestBuildPlanStyle;
  return lotType?.includes('Thrive');
};

export const isAllowedToEditUnderContract = (user: IUser): boolean => {
  return user?.userRoles?.some(role =>
    [
      UserRoles.BLAdmin,
      UserRoles.BLSalesAdmin,
      UserRoles.BLSalesCoordinator,
      UserRoles.BLClosing
    ].includes(role as UserRoles)
  );
};

export const HOMEOWNERS_PORTAL_SLUG = 'homeowner-portal';
export const HOMEOWNERS_PORTAL_THRIVE_SLUG = 'homeowner-portal-thrive';
export const HELP_SLUG = 'how-to';
export const SALES_APP_SLUG = 'sales-app';
export const OSC_REPORT_SLUG = 'osc-report';

export const getUserInitials = (userName: string | undefined | null) => {
  if (!userName?.trim().length) {
    return '?';
  }
  const nameParts = userName.split(' ').filter(s => !!s.trim());
  const initials = [];
  initials.push(nameParts[0][0]);
  if (nameParts.length > 1) {
    initials.push(nameParts[nameParts.length - 1][0]);
  }
  return initials.join('').toUpperCase();
};
